import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check, ChevronLeft, ChevronRight, ShieldCheck, Upload } from "lucide-react";
import { Badge } from "../lib/ui";
import EvidenceIntake from "../components/EvidenceIntake";
import DataRowTable from "../components/DataRowTable";
import RowActionBar from "../components/RowActionBar";
import ModalScrim from "../components/ModalScrim";
import StorySpine from "../components/StorySpine";
import SubFlowNav, { FocusPanel, MetricStrip, SimpleBars } from "../components/SubFlow";
import { profilePassport, useAppGo } from "../lib/story";
import { subflowHref, useSubFlow } from "../lib/subflow";
import { canWriteEvidence, canWriteWhy, navItemAllowed } from "../lib/master";
import { isPreG1 } from "../lib/g1Journey";
import { ASSESSMENT_DRILL, ASSESSMENT_FLOWS, ASSESSMENT_FOCUS_DETAIL } from "../lib/assessmentFlows";

const STATE_TONE = {
  VERIFIED: "green", KNOWN: "blue", UNKNOWN: "amber", CONFLICTING: "rose",
  STALE: "amber", INFERRED: "slate", PENDING_CONFIRMATION: "amber", NOT_APPLICABLE: "slate",
};

export default function Assessment({ app, onAddEvidence, onIngest, onSaveWhy, onDecideG1, user }) {
  const go = useAppGo(app?.id);
  const { focus } = useSubFlow("overview");
  const [domainId, setDomainId] = useState("why");
  const [viewFact, setViewFact] = useState(null);
  const [intakeOpen, setIntakeOpen] = useState(false);

  useEffect(() => {
    if (focus === "why") setDomainId("why");
  }, [focus]);

  if (!app) return null;
  const isOverview = focus === "overview";
  const guide = user?.guide || {};
  const mayWriteWhy = canWriteWhy(guide);
  const mayIngest = canWriteEvidence(guide);
  const mayDecide = navItemAllowed("/decision", guide) && !isPreG1(app);
  const mayG1Decide = (user?.access?.decision_authorities?.G1 === "APPROVE" || (user?.access?.may_approve || []).includes("G1")) && onDecideG1;
  const g1Sealed = app.g1_decision?.outcome;
  const g1 = app.g1_journey || {};
  const eq = g1.evidence_quality || {};
  const whyTrace = g1.why_trace || app.why_trace || {};
  const g1Options = g1.governance_config?.decision_options || [];
  const negativeOutcomes = g1.governance_config?.negative_outcomes || [];
  const pain = app.before?.pain || [];

  const foundation = app.input_foundation;
  const readiness = foundation?.readiness;
  const domains = foundation?.domains || [];
  const selected = domains.find((d) => d.id === domainId) || domains[0];
  const facts = selected?.facts || [];
  const trace7c = app.assessment_trace?.["7c"] || app.assessments?.["7c"] || [];
  const trace7s = app.assessment_trace?.["7s"] || app.assessments?.["7s"] || [];
  const strategies = app.strategy_trace || [];
  const why = app.why || {};

  return (
    <div className="inner-page subflow-page" data-testid="assessment-page">
      {!isOverview && (
        <Link to={subflowHref("/assessment", app.id, "overview")} className="analytics-back" data-testid="assessment-back">
          <ChevronLeft size={16} /> Back to assessment overview
        </Link>
      )}

      <FocusPanel
        kicker={`ASSESS · ${app.id}`}
        title={isOverview ? "Pre-G1 assessment" : (ASSESSMENT_FLOWS.find(f => f.id === focus)?.label || "Assessment")}
        detail={ASSESSMENT_FOCUS_DETAIL[focus] || "WHY is business intent. Evidence quality feeds confidence — not authorization."}
        badge={<Badge tone={readiness?.eligible_for_authorization ? "green" : "amber"}>{readiness?.eligible_for_authorization ? "Ready to decide" : "Evidence gaps"}</Badge>}
        testId="assessment-head"
      />

      {!isOverview && (
        <>
          <SubFlowNav items={ASSESSMENT_FLOWS} focus={focus} appId={app.id} testId="assessment-subflow" />
          <StorySpine />
        </>
      )}

      {isOverview && (
        <>
          <MetricStrip items={[
            { label: "Coverage", value: `${readiness?.coverage || 0}%`, testId: "assessment-overview-coverage" },
            { label: "Unknown", value: readiness?.unknown ?? "—", testId: "assessment-overview-unknown" },
            { label: "G1", value: g1.status || "NOT_READY", testId: "assessment-overview-g1" },
            { label: "WHY", value: g1.why_state || "—", testId: "assessment-overview-why" },
          ]} />
          <section className="feature-panel analytics-panel" data-testid="assessment-drill">
            <p className="kicker">DRILL DOWN · ONE TOPIC PER SCREEN</p>
            <div className="lifecycle-phase-links">
              {ASSESSMENT_DRILL.map(({ id, label }) => (
                <Link key={id} to={subflowHref("/assessment", app.id, id)} className="lifecycle-phase-link" data-testid={`assessment-drill-${id}`}>
                  <strong>{label}</strong>
                  <span>{ASSESSMENT_FOCUS_DETAIL[id]}</span>
                  <ChevronRight size={16} />
                </Link>
              ))}
            </div>
          </section>
        </>
      )}

      {focus === "readiness" && (
        <FocusPanel kicker="EVIDENCE QUALITY" title="Can a human authorize?" testId="assessment-readiness-panel">
          {readiness && (
            <>
              <MetricStrip items={[
                { label: "Coverage", value: `${readiness.coverage}%`, testId: "assessment-readiness" },
                { label: "Verified", value: readiness.verified, testId: "metric-verified" },
                { label: "Unknown", value: readiness.unknown, detail: "Never coerced", testId: "metric-unknown" },
                { label: "Conflicting", value: readiness.conflicting, testId: "metric-conflicting" },
              ]} />
              <SimpleBars rows={[
                { label: "Verified", value: readiness.verified || 0, tone: "green", testId: "bar-verified" },
                { label: "Known", value: readiness.known || 0, tone: "blue", testId: "bar-known" },
                { label: "Unknown", value: readiness.unknown || 0, tone: "amber", testId: "bar-unknown" },
                { label: "Stale", value: readiness.stale || 0, tone: "amber", testId: "bar-stale" },
              ]} />
              <div className={`readiness-eligible ${readiness.eligible_for_authorization ? "yes" : "no"}`} data-testid="assessment-auth-eligible">
                <ShieldCheck size={14} />
                {readiness.eligible_for_authorization ? "Eligible for human authorization" : "Not eligible — critical evidence unresolved"}
              </div>
              {readiness.note && <p className="muted small-note">{readiness.note}</p>}
            </>
          )}
          <div className="focus-actions">
            {mayIngest && <button data-testid="assessment-open-ingest" className="secondary-button" type="button" onClick={() => setIntakeOpen(true)}><Upload size={15} /> Ingest evidence</button>}
            {mayDecide && <button data-testid="assessment-open-decision" className="primary-button" type="button" onClick={() => go("/decision?focus=verdict")}>Open 7R decision <ArrowUpRight size={15} /></button>}
          </div>
        </FocusPanel>
      )}

      {focus === "why" && (
        <div id="spine-why" className="why-banner why-loop-start" data-testid="assessment-why-start">
          <FocusPanel kicker="WHY · BUSINESS INTENT" title="What problem must this decision solve?" testId="assessment-why-panel">
            {(whyTrace.chain || []).length > 0 && (
              <div className="admin-note" data-testid="why-trace-inline">
                <p className="kicker">{whyTrace.sequence}</p>
                {(whyTrace.chain || []).map((level) => (
                  <p key={level.level} className="muted small-note">
                    <strong>{level.level}:</strong> {level.driver || level.problem || level.label || "—"}
                  </p>
                ))}
              </div>
            )}
            {mayWriteWhy ? (
              <WhyForm key={app.id} why={why} sponsor={app.sponsor} onSave={onSaveWhy} />
            ) : (
              <>
                <p>{why.problem}</p>
                <p className="muted small-note"><strong>Driver:</strong> {why.driver} · <strong>Risk of doing nothing:</strong> {why.risk_of_nothing}</p>
              </>
            )}
            <div className="outcome-list">
              {(why.outcomes || []).map((o) => <span key={o}><Check size={14} /> {o}</span>)}
            </div>
          </FocusPanel>
        </div>
      )}

      {focus === "pain" && (
        <FocusPanel kicker="PAIN & CHALLENGES" title="What is wrong — not what exists" testId="assessment-pain-panel">
          <p className="muted small-note">Pain must link to evidence where possible. Unsubstantiated claims stay visible as gaps.</p>
          {pain.length ? (
            <ul className="admin-list">
              {pain.map((item) => <li key={item}>{item}</li>)}
            </ul>
          ) : (
            <p className="muted">No pain recorded yet. Capture business, technology, operational or delivery pain during discovery workshops.</p>
          )}
        </FocusPanel>
      )}

      {focus === "g1" && (
        <FocusPanel
          kicker="G1 · MODERNIZATION INTENT"
          title="Human review — minimum sufficient evidence"
          detail={g1.governance_config?.purpose || g1.definition}
          badge={<Badge tone={g1.status === "PASS" ? "green" : "amber"}>{g1.status || "NOT_READY"}</Badge>}
          testId="assessment-g1-panel"
        >
          <p className="muted small-note">
            G1 is not 7R, MDR, wave or architecture approval. AI advisory only.
            Legitimate outcomes include defer and do not modernize — not only PASS.
          </p>
          <div className="metric-grid">
            <div className="metric"><div><small>Completeness</small><strong>{eq.completeness?.pct ?? 0}%</strong></div></div>
            <div className="metric"><div><small>Confidence</small><strong>{eq.confidence?.pct ?? 0}%</strong></div></div>
            <div className="metric"><div><small>G1 readiness</small><strong>{eq.decision_readiness?.pct ?? g1.readiness_pct ?? 0}%</strong></div></div>
            <div className="metric"><div><small>WHY</small><strong>{g1.why_state || "—"}</strong></div></div>
          </div>
          {g1.decision_fork && (
            <div className="g1-decision-fork" data-testid="assessment-g1-fork">
              <p className="kicker">AFTER HUMAN DECISION</p>
              <p className="muted small-note">
                <strong>{g1.decision_fork.proceed?.label}:</strong> {g1.decision_fork.proceed?.note}
              </p>
              <p className="muted small-note">
                <strong>{g1.decision_fork.do_not_proceed?.label}:</strong> {g1.decision_fork.do_not_proceed?.note}
              </p>
            </div>
          )}
          {g1Options.length > 0 && (
            <>
              <p className="kicker">DECISION OPTIONS</p>
              <div className="outcome-list">
                {g1Options.map((opt) => (
                  <span key={opt} className={negativeOutcomes.includes(opt) ? "muted" : ""}>{opt}</span>
                ))}
              </div>
            </>
          )}
          {(g1.missing || []).length > 0 && (
            <>
              <p className="kicker">STILL MISSING</p>
              <ul className="admin-list">{g1.missing.map((m) => <li key={m}>{m}</li>)}</ul>
            </>
          )}
          <p className="admin-note">Outcomes: {(why.outcomes || []).join(" · ") || "Not defined"}</p>
          {g1Sealed && (
            <article className="admin-card" data-testid="assessment-g1-sealed">
              <strong>G1 sealed: {g1Sealed}</strong>
              <p className="muted small-note">{app.g1_decision?.rationale}</p>
              {app.g1_decision?.conditions && <p className="muted small-note">Conditions: {app.g1_decision.conditions}</p>}
              <small>{app.g1_decision?.actor} · {app.g1_decision?.hash}</small>
            </article>
          )}
          {mayG1Decide && !g1Sealed && (
            <G1DecisionForm
              options={g1Options.length ? g1Options : ["PROCEED", "PROCEED WITH CONDITIONS", "DEFER", "RETURN FOR EVIDENCE", "NOT A MODERNIZATION CANDIDATE"]}
              blockers={g1.blockers || []}
              user={user}
              onDecide={onDecideG1}
            />
          )}
        </FocusPanel>
      )}

      {focus === "domain" && (
        <div id="spine-assess" data-testid="assessment-assess-start">
          <FocusPanel kicker={`DOMAIN · ${selected?.title || ""}`} title={selected?.purpose || "Pick a domain"} detail={selected ? `Affects: ${selected.affects}` : ""} testId="assessment-domain-panel">
            <aside className="domain-rail compact" data-testid="assessment-domain-rail">
              {domains.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  data-testid={`assessment-domain-${d.id}`}
                  className={selected?.id === d.id ? "domain-tab active" : "domain-tab"}
                  onClick={() => { setDomainId(d.id); setViewFact(null); }}
                >
                  <strong>{d.title}</strong>
                  <small>{d.fact_count} facts · {d.counts?.unknown || 0} unknown</small>
                </button>
              ))}
            </aside>
            <DataRowTable
              testId="assessment-foundation-table"
              columns={[
                { key: "label", label: "Fact", render: (f) => <b>{f.label}</b> },
                { key: "value", label: "Value" },
                { key: "state", label: "State", render: (f) => <Badge tone={STATE_TONE[f.state] || "slate"}>{f.state}</Badge> },
                { key: "decision_impact", label: "Impact" },
              ]}
              rows={facts}
              rowKey="id"
              pageSize={10}
              hint="View opens fact detail. Review flagged rows with UNKNOWN or CONFLICTING state."
              renderActions={(row, stableId) => (
                <RowActionBar
                  testId={`assessment-fact-${stableId}`}
                  rowId={stableId}
                  view
                  review={row.state === "UNKNOWN" || row.state === "CONFLICTING"}
                  onView={() => setViewFact(row)}
                  onReview={() => setViewFact(row)}
                />
              )}
            />
          </FocusPanel>
        </div>
      )}

      {focus === "lenses" && !isPreG1(app) && (
        <FocusPanel kicker="7C / 7S" title="How evidence ranks alternatives" testId="assessment-7c7s-trace">
          <div className="assessments-grid">
            <div className="assessments-col">
              <h4>7C</h4>
              {trace7c.map((a) => (
                <div className="assessment-row" key={a.lens}>
                  <span className="assessment-lens">{a.lens}</span>
                  <span className="assessment-bar-wrap"><span className="assessment-bar" style={{ width: `${a.score}%` }} /></span>
                  <b>{a.score}</b>
                </div>
              ))}
            </div>
            <div className="assessments-col">
              <h4>7S</h4>
              {trace7s.map((a) => (
                <div className="assessment-row" key={a.lens}>
                  <span className="assessment-lens">{a.lens}</span>
                  <span className="assessment-bar-wrap"><span className="assessment-bar cyan" style={{ width: `${a.score}%` }} /></span>
                  <b>{a.score}</b>
                </div>
              ))}
            </div>
          </div>
          <div className="strategy-trace">
            {strategies.map((s) => (
              <div key={s.strategy} className={`strategy-row verdict-${s.verdict}`} data-testid={`strategy-trace-${(s.strategy || "").toLowerCase().replaceAll(" ", "-").replaceAll("/", "-")}`}>
                <span className="strategy-mark">{s.mark}</span>
                <div><strong>{s.strategy}</strong><p>{s.reason}</p></div>
              </div>
            ))}
          </div>
        </FocusPanel>
      )}

      {focus === "passport" && (
        <FocusPanel kicker="APPLICATION 360°" title="What we know — no 7R here" testId="application-passport">
          <p className="muted small-note">
            {app.passport?._meta?.note || "Passport enriches progressively from evidence — not disconnected questionnaires."}
          </p>
          <div className="passport-grid">
            {profilePassport(app.passport).map(([key, val]) => (
              <div key={key}>
                <small>{key.toUpperCase()}</small>
                <p>{val || "Unknown"}</p>
              </div>
            ))}
          </div>
        </FocusPanel>
      )}

      {mayIngest && intakeOpen && (
        <EvidenceIntake appId={app.id} onClose={() => setIntakeOpen(false)} onManual={onAddEvidence} onIngest={onIngest} />
      )}

      {viewFact && (
        <ModalScrim testId={`assessment-fact-view-${viewFact.id}`} onBackdropClick={() => setViewFact(null)}>
          <div className="modal wide" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="modal-close" onClick={() => setViewFact(null)} aria-label="Close">×</button>
            <p className="kicker">FOUNDATION FACT · {viewFact.id}</p>
            <h2>{viewFact.label}</h2>
            <dl className="admin-review-dl">
              {[["Value", viewFact.value], ["State", viewFact.state], ["Impact", viewFact.decision_impact], ["Domain", selected?.title]].map(([label, val]) => (
                val != null && String(val).trim() !== "" && (
                  <div key={label} className="admin-review-row"><dt>{label}</dt><dd>{String(val)}</dd></div>
                )
              ))}
            </dl>
            <button type="button" className="primary-button" onClick={() => setViewFact(null)}>Close</button>
          </div>
        </ModalScrim>
      )}
    </div>
  );
}

function G1DecisionForm({ options, blockers, user, onDecide }) {
  const [outcome, setOutcome] = useState(options[0] || "PROCEED WITH CONDITIONS");
  const [rationale, setRationale] = useState("");
  const [conditions, setConditions] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const sessionRole = user?.role || user?.access?.user?.role || "—";
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await onDecide({
        outcome,
        rationale,
        approver: user?.name,
        conditions: conditions || undefined,
      });
    } catch (ex) {
      setErr(ex.response?.data?.detail || "G1 decision failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="why-form" data-testid="g1-decision-form" onSubmit={submit}>
      <p className="kicker">HUMAN G1 DECISION · IMMUTABLE</p>
      <p className="muted small-note">Authority: {sessionRole} (session role — not selectable)</p>
      {blockers.length > 0 && (
        <p className="admin-note">Blockers: {blockers.join(" · ")} — PASS outcomes will be rejected until resolved.</p>
      )}
      <label>Outcome
        <select data-testid="g1-outcome" value={outcome} onChange={(e) => setOutcome(e.target.value)}>
          {options.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
        </select>
      </label>
      <label>Rationale<textarea data-testid="g1-rationale" required rows={3} value={rationale} onChange={(e) => setRationale(e.target.value)} /></label>
      <label>Conditions (optional)<textarea data-testid="g1-conditions" rows={2} value={conditions} onChange={(e) => setConditions(e.target.value)} /></label>
      {err && <div className="error-message">{err}</div>}
      <button data-testid="g1-submit" className="primary-button" type="submit" disabled={busy}>{busy ? "Sealing…" : "Seal G1 decision"}</button>
    </form>
  );
}

function WhyForm({ why, sponsor, onSave }) {
  const [driver, setDriver] = useState(why.driver || "");
  const [problem, setProblem] = useState(why.problem || "");
  const [risk, setRisk] = useState(why.risk_of_nothing || "");
  const [outcomes, setOutcomes] = useState((why.outcomes || []).join("\n"));
  const [kpis, setKpis] = useState((why.kpis || []).join("\n"));
  const [owner, setOwner] = useState(sponsor || "");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await onSave({ driver, problem, risk_of_nothing: risk, outcomes, kpis, sponsor: owner });
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Could not save WHY");
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="why-form" data-testid="why-form" onSubmit={submit}>
      <label>Driver<input data-testid="why-driver" required value={driver} onChange={(e) => setDriver(e.target.value)} /></label>
      <label>Business problem<textarea data-testid="why-problem" required rows={2} value={problem} onChange={(e) => setProblem(e.target.value)} /></label>
      <label>Risk of doing nothing<textarea data-testid="why-risk" rows={2} value={risk} onChange={(e) => setRisk(e.target.value)} /></label>
      <label>Outcomes (one per line)<textarea data-testid="why-outcomes" required rows={3} value={outcomes} onChange={(e) => setOutcomes(e.target.value)} /></label>
      <label>KPIs (one per line)<textarea data-testid="why-kpis" rows={2} value={kpis} onChange={(e) => setKpis(e.target.value)} /></label>
      <label>Named sponsor<input data-testid="why-sponsor" required value={owner} onChange={(e) => setOwner(e.target.value)} /></label>
      {err && <div className="error-message">{err}</div>}
      <button data-testid="why-save" className="primary-button" type="submit" disabled={busy}>{busy ? "Saving…" : "Save WHY"}</button>
    </form>
  );
}
